import { useEffect, useRef } from 'react';
import { useParams, useMatch, Outlet } from 'react-router-dom';
import {
  PermissionDenied,
  PermissionEnum,
  SchemaFilterType,
  Table,
} from '@ui/src';
import { useAppSelector } from '@store';
import { columns, columnsLabels } from './variables/list';
import { OrganizationType } from '../types';
import { GeneralInfo } from '../../individual/types';
import { CorporateGeneralInfo } from '../../corporate/types';
import useBeneficiariesList from './hooks/use-list';

import ROUTES from '../../../routes';

import cn from './styles.module.css';

const Beneficiaries = ({ type }: { type: OrganizationType }) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const { customerId } = useParams();

  const match = useMatch(
    type === OrganizationType.INDIVIDUAL
      ? ROUTES.CUSTOMERS_INDIVIDUAL_BENEFICIARIES
      : ROUTES.CUSTOMERS_CORPORATE_BENEFICIARIES,
  );

  const { beneficiaries, pagination, beneficiariesErrorsList } = useAppSelector(
    (state) => state.customerBeneficiaries,
  );
  const { baseInformation: corporateBaseInformation } = useAppSelector(
    (state) => state.corporateCustomers.info.general,
  );
  const { baseInformation: individualBaseInformation } = useAppSelector(
    (state) => state.individualCustomers.info.general,
  );
  const { activePermissions } = useAppSelector((state) => state.root);

  const baseInformation =
    type === OrganizationType.INDIVIDUAL
      ? individualBaseInformation
      : corporateBaseInformation;

  const {
    formattedData,
    filters,
    onFilter,
    takeCount,
    filtersComponent,
    handleSubmit,
    isLoading,
    isFetching,
    setPage,
  } = useBeneficiariesList({
    scrollRef,
    beneficiaries,
    type,
    customerId,
  });

  useEffect(() => {
    let title: string;

    const companyName = localStorage.getItem('companyName');

    if (type === OrganizationType.INDIVIDUAL) {
      if (
        (baseInformation as Partial<GeneralInfo>)?.firstName ||
        (baseInformation as Partial<GeneralInfo>)?.lastName
      ) {
        title = `${(baseInformation as Partial<GeneralInfo>)?.firstName} ${(baseInformation as Partial<GeneralInfo>)?.lastName}: Beneficiaries`;
      } else {
        title = 'Beneficiaries';
      }
    } else if ((baseInformation as Partial<CorporateGeneralInfo>)?.name) {
      title = `${(baseInformation as Partial<CorporateGeneralInfo>)?.name}: Beneficiaries`;
    } else {
      title = 'Beneficiaries';
    }

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, [baseInformation]);

  let content;

  if (!activePermissions?.includes(PermissionEnum.BENEFICIARIES_LIST)) {
    content = <PermissionDenied />;
  } else {
    content = (
      <>
        <div className={cn.beneficiariesTitle}>Beneficiaries</div>
        <Table
          columns={columns}
          data={formattedData || []}
          onSort={() => {}}
          filters={filters}
          sort={{}}
          columnsLabels={columnsLabels}
          tableClassName={!beneficiaries?.length ? cn.beneficiariesEmpty : ''}
          tableTopClassName={cn.beneficiaryTableTop}
          onFilterChange={(values) =>
            onFilter({
              ...values,
              organizationId: {
                value: [customerId as string],
                type: SchemaFilterType.STRING,
              },
              take: {
                value: [takeCount.toString()],
                type: SchemaFilterType.STRING,
              },
            })
          }
          filtersComponent={filtersComponent}
          handleSubmit={handleSubmit}
          isLoading={isLoading || isFetching}
          take={takeCount.toString()}
          setPage={(data) => {
            setPage({
              ...data,
              filters: {
                ...filters,
                organizationId: {
                  value: [customerId as string],
                  type: SchemaFilterType.STRING,
                },
              },
            });
          }}
          error={beneficiariesErrorsList}
          pagination={pagination}
          clickable
        />
      </>
    );
  }

  return (
    <div className={cn.beneficiariesWrapper} ref={scrollRef}>
      {match ? content : <div />}
      <Outlet />
    </div>
  );
};

export default Beneficiaries;
